import React, { useMemo } from 'react'
import { Alert, Button, Spin, Typography } from '@surecompanies/core_components'
import { ProfileOutlined } from '@ant-design/icons'
import { usePaymentBotCarrierSummaryQuery } from '../../../../api/payment-bot-dashboard-api-client'
import { computeDashboardSummary } from '../../shared/utils'
import { PaymentBotKpiStrip } from './PaymentBotKpiStrip'
import { PaymentBotCarrierTable } from './PaymentBotCarrierTable'
import styles from './PaymentBotSummary.module.scss'

const { Text, Title } = Typography

interface PaymentBotSummaryProps {
  month: number
  year: number
  onSelectCarrier: (carrierId: number) => void
  onViewAllCarriers: () => void
}

export function PaymentBotSummary({
  month,
  year,
  onSelectCarrier,
  onViewAllCarriers,
}: PaymentBotSummaryProps) {
  const { data, isLoading, isFetching, isError, error } =
    usePaymentBotCarrierSummaryQuery({ month, year })

  const rows = useMemo(() => data?.carriers ?? [], [data])
  const summary = useMemo(
    () =>
      computeDashboardSummary(rows, data?.overallMedianProcessingTimeSec ?? 0),
    [rows, data?.overallMedianProcessingTimeSec]
  )

  const { overallFirstSucceeded, overallRescued } = useMemo(() => {
    // Use completedCount+failedCount as denominator weight — same as firstAttemptRate's denominator.
    // totalBenefits includes PENDING which firstAttemptRate doesn't account for.
    const firstSucceeded = rows.reduce(
      (sum, r) =>
        sum +
        Math.round(
          (r.firstAttemptRate * (r.completedCount + r.failedCount)) / 100
        ),
      0
    )
    return {
      overallFirstSucceeded: firstSucceeded,
      overallRescued: Math.max(0, summary.succeededBenefits - firstSucceeded),
    }
  }, [rows, summary])

  if (isLoading) {
    return (
      <div
        className={styles.loadingWrap}
        data-testid="payment-bot-dashboard-loading"
      >
        <Spin size="large" />
      </div>
    )
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load payment bot metrics"
        description={error?.message}
        data-testid="payment-bot-dashboard-error"
      />
    )
  }

  return (
    <div data-testid="payment-bot-dashboard">
      <div className={styles.header}>
        <Text type="secondary">
          Real-time monitoring of automated payment processes
        </Text>
      </div>

      <PaymentBotKpiStrip
        summary={summary}
        overallFirstSucceeded={overallFirstSucceeded}
        overallRescued={overallRescued}
      />

      <div className={styles.sectionHeader}>
        <Title level={4} style={{ margin: 0 }}>
          Payment Performance
        </Title>
        <Button
          icon={<ProfileOutlined />}
          onClick={onViewAllCarriers}
          data-testid="all-carrier-detail-button"
        >
          All-carrier detail
        </Button>
      </div>

      <PaymentBotCarrierTable
        rows={rows}
        isFetching={isFetching}
        onSelectCarrier={onSelectCarrier}
      />
    </div>
  )
}
